/**
 * How far a chart toolbar folds to fit its cell (owner, 2026-10-03).
 *
 * The full row needs ~800px — its controls are fixed widths and both sides are
 * `max-content` — so a cell in a four-across layout clipped everything right of
 * the symbol, ⛶ included. The row now measures itself and folds the least-used
 * controls first. Nothing is removed, only put behind a key; ⛶ and the badge's
 * dot never fold, because full screen is the way out of a cramped cell.
 *
 *   0  ≥ 800  everything, as before
 *   1  ≥ 600  the date box → 📅 key; the badge → its dot
 *   2  ≥ 420  the recent intervals and the count box go (▾ lists them all);
 *             the bars box moves into the 📅 popover; the symbol box narrows
 *   3  < 420  the symbol shows its ticker only, and is no longer centred
 *
 * Measured from the toolbar, never from the chart count: four charts on a 4K
 * screen keep the full row. The thresholds are a first guess, to be tuned on a
 * real screen.
 */

export type ToolbarFold = 0 | 1 | 2 | 3;

export const FOLD_AT = [800, 600, 420] as const;

export function toolbarFold(width: number): ToolbarFold {
  // A width of 0 is an element not laid out yet (hidden tab, first frame):
  // assume the full row rather than flash the folded one.
  if (width <= 0 || width >= FOLD_AT[0]) return 0;
  if (width >= FOLD_AT[1]) return 1;
  if (width >= FOLD_AT[2]) return 2;
  return 3;
}

/** The symbol box's own horizontal padding, 0.4rem a side, in px. */
const BOX_PADDING = 13;
/** Text room below which a name cannot follow the ticker legibly. */
const NAME_ROOM = 110;

/**
 * Whether the symbol box shows the ticker alone.
 *
 * Always at the last fold. At fold 2 only when the box, having grown into the
 * row's free space, still leaves too little room once the star and the lock have
 * taken theirs on both sides (`inner`, px a side) — so a cramped box reads "XLK"
 * rather than "X.." (owner, 2026-10-06, 4×3). An unmeasured box (0) keeps the
 * name, which is what the wider folds show.
 */
export function showsTickerOnly(fold: ToolbarFold, boxWidth: number, inner: number): boolean {
  if (fold >= 3) return true;
  if (fold < 2 || boxWidth <= 0) return false;
  return boxWidth - 2 * inner - BOX_PADDING < NAME_ROOM;
}

/** The full row's box at rest: what `FOLD_AT[0]` was counted with. */
export const SYMBOL_REST = 220;

/**
 * The width the full row's symbol box asks for: its whole label (owner,
 * 2026-10-09: "RELY - Re…" and "TWLO - Tw…" in a fixed 220px box between two
 * empty strips). Never below 220. **No ceiling here** — the box shrinks in the
 * row's flex layout when the space between the date box and LIVE runs out,
 * because both sides are `max-content` and the box is the one thing that may
 * give. `textPx` 0 (unmeasured) is the box at rest.
 */
export function fullSymbolWidth(textPx: number, inner: number): number {
  if (textPx <= 0) return SYMBOL_REST;
  return Math.max(SYMBOL_REST, Math.ceil(textPx) + 2 * inner + 2 * BOX_PADDING + 4);
}
